import { Badge, EmptyState, Icon } from '@/components/ui';
import { conversationSubject, isSideThread } from '@/features/messages/subject';
import { cn } from '@/utils/cn';
import { relativeTime } from '@/utils/format';

/** Two letters is enough identity for a 40px circle. */
function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * The threads on one side, most recent first.
 *
 * It shows the other party, never the caller — a student sees the company and
 * a company sees the candidate. Nobody needs to be told who they are. Under
 * the name, what the thread is about: the role, or "Question about …" for a
 * question asked before applying.
 */
export function ConversationList({ conversations, selectedId, onSelect }) {
  if (!conversations.length) {
    return (
      <EmptyState
        icon={<Icon name="messages" size={22} />}
        title="No conversations yet"
        description="A conversation starts when somebody sends the first message."
      />
    );
  }

  return (
    <ul className="divide-line divide-y">
      {conversations.map((conversation) => {
        const isSelected = conversation.id === selectedId;
        const unread = conversation.unread_count > 0;
        const subject = conversationSubject(conversation);
        return (
          <li key={conversation.id}>
            <button
              type="button"
              onClick={() => onSelect(conversation.id)}
              aria-current={isSelected ? 'true' : undefined}
              className={cn(
                'relative flex w-full gap-3 px-4 py-3.5 text-left transition',
                isSelected ? 'bg-brand-50' : 'hover:bg-surface-muted',
              )}
            >
              {/* The selected thread is marked by a bar as well as a tint,
                  so it does not rely on a colour difference alone. */}
              {isSelected && (
                <span
                  aria-hidden="true"
                  className="bg-brand-600 absolute inset-y-2 left-0 w-1 rounded-r-full"
                />
              )}
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-10 shrink-0 place-items-center rounded-full text-xs font-semibold',
                  isSelected || unread ? 'bg-brand-600 text-white' : 'bg-brand-100 text-brand-700',
                )}
              >
                {initials(conversation.counterpart_name)}
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span
                    className={cn(
                      'text-ink truncate text-sm',
                      unread ? 'font-semibold' : 'font-medium',
                    )}
                  >
                    {conversation.counterpart_name}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-[11px]',
                      unread ? 'text-brand-700 font-semibold' : 'text-ink-muted',
                    )}
                  >
                    {relativeTime(conversation.last_message_at)}
                  </span>
                </span>
                {subject && (
                  <span
                    className={cn(
                      'mt-0.5 flex items-center gap-1 truncate text-xs',
                      isSideThread(conversation) ? 'text-brand-700' : 'text-ink-muted',
                    )}
                  >
                    {isSideThread(conversation) && (
                      <Icon name="chat" size={13} className="shrink-0" />
                    )}
                    <span className="truncate">{subject}</span>
                  </span>
                )}
                <span className="mt-1 flex items-center gap-2">
                  <span
                    className={cn(
                      'flex-1 truncate text-xs',
                      unread ? 'text-ink' : 'text-ink-muted',
                    )}
                  >
                    {conversation.last_message_preview ?? 'No messages yet'}
                  </span>
                  {unread && (
                    <Badge tone="brand" className="bg-brand-600 px-2 text-white ring-0">
                      {conversation.unread_count}
                    </Badge>
                  )}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
